import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Customer, customers as customersApi, Geofence, GEOFENCE_COLOURS, GeofenceColour, GeofenceEvent, geofences, GeofenceInput } from '../api';
import { useMapEngine } from '../components/FleetMap';
import { Circle, GeofenceEditor, Points } from '../components/GeofenceEditor';
import { reloadGeofences, useGeofences } from '../components/geofenceStore';
import { Link } from '../components/Links';
import { SearchSelect } from '../components/SearchSelect';
import { dateTime } from '../format';
import { areaText, ownerText, Swatch } from './Geofences';

const COLOUR_NAME: Record<GeofenceColour, string> = { blue: 'Blue', green: 'Green', orange: 'Orange', purple: 'Purple', red: 'Red', teal: 'Teal' };
type Loaded = Geofence & { events: Array<GeofenceEvent & { vehicle: { id: number; name: string } }> };

// Add or change one geofence: name, colour, owner and the shape drawn on the map.
export function GeofenceEdit() {
  const { id: idParam } = useParams();
  const id = idParam === 'new' ? null : Number(idParam);
  const navigate = useNavigate();
  // Back to where the editor was opened from (the map's Geofences card or the list).
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/geofences'));
  const engine = useMapEngine();
  const all = useGeofences();
  const [cur, setCur] = useState<Loaded | null>(null);
  const [custs, setCusts] = useState<Customer[]>([]);
  const [name, setName] = useState('');
  const [colour, setColour] = useState<GeofenceColour>('blue');
  const [owner, setOwner] = useState('');           // '' = company-wide
  const [kind, setKind] = useState<'circle' | 'polygon'>('circle');
  const [circle, setCircle] = useState<Circle | null>(null);
  const [points, setPoints] = useState<Points>([]);
  const [shapeTouched, setShapeTouched] = useState(false);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Array<{ label: string; lat: number; lon: number }> | null>(null);
  const [goTo, setGoTo] = useState<{ key: number; lat: number; lon: number } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  useEffect(() => {
    if (id === null) return;
    geofences.get(id).then((g) => {
      setCur(g);
      setName(g.name);
      setColour(g.colour);
      setOwner(g.customer ? String(g.customer.id) : '');
      setKind(g.kind);
      setCircle(g.circle);
      setPoints(g.kind === 'polygon' ? g.points : []);
    }).catch((e) => setError(e.message));
  }, [id]);

  const others = useMemo(() => all.filter((g) => g.id !== id).map((g) => ({ id: g.id, points: g.points })), [all, id]);
  const ownerOptions = useMemo(() => custs.map((c) => ({ value: String(c.id), label: c.name })), [custs]);
  const shapeReady = kind === 'circle' ? circle !== null : points.length >= 3;

  const findPlace = async (e: FormEvent) => {
    e.preventDefault();
    setFound(null);
    try {
      const r = await geofences.searchPlaces(search);
      setFound(r.results);
      if (r.results.length === 1) setGoTo({ key: Date.now(), ...r.results[0] });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!shapeReady) { setError(kind === 'circle' ? 'Click on the map to place the circle.' : 'Click on the map for at least 3 corners.'); return; }
    setBusy(true);
    setError('');
    const body: GeofenceInput = { name: name.trim(), colour, customerId: owner ? Number(owner) : null };
    if (id === null || shapeTouched) Object.assign(body, kind === 'circle' ? { circle: circle! } : { points });
    try {
      if (id === null) {
        await geofences.add(body);
        void reloadGeofences();
        back();
      } else {
        await geofences.update(id, body);
        void reloadGeofences();
        back();
      }
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await geofences.remove(id!);
      void reloadGeofences();
      back();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  if (id !== null && !cur && !error) return <div className="page"><p className="muted">Loading…</p></div>;
  if (id !== null && !cur) return <div className="page"><p className="error" role="alert">{error}</p></div>;

  return (
    <div className="page wide">
      <p className="crumbs"><Link to="/geofences">Geofences</Link></p>
      <div className="page-head">
        <h1>{id === null ? 'Add a geofence' : cur!.name}</h1>
      </div>
      <form className="geo-edit" onSubmit={save}>
        <div className="card form geo-edit-fields">
          <label>Name<input id="g-name" type="text" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Letterkenny yard" /></label>
          <label htmlFor="g-owner">Owner</label>
          <SearchSelect id="g-owner" value={owner} options={ownerOptions} emptyLabel="Company-wide (staff only)" onChange={setOwner} />
          <p className="hint">Customers see only their own geofences. Company-wide ones are never shown to them.</p>
          <fieldset className="geo-colours">
            <legend>Colour</legend>
            {GEOFENCE_COLOURS.map((c) => (
              <label key={c} className="check geo-colour">
                <input type="radio" name="g-colour" value={c} checked={colour === c} onChange={() => setColour(c)} />
                <Swatch colour={c} />{COLOUR_NAME[c]}
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Shape</legend>
            <div className="seg" role="group" aria-label="Shape">
              <button type="button" aria-pressed={kind === 'circle'} onClick={() => { setKind('circle'); setShapeTouched(true); }}>Circle</button>
              <button type="button" aria-pressed={kind === 'polygon'} onClick={() => { setKind('polygon'); setShapeTouched(true); }}>Polygon</button>
            </div>
            {kind === 'circle' ? (
              <>
                <p className="hint">Click on the map for the centre, then drag the centre or the edge.</p>
                <label>Radius (m)<input id="g-radius" type="number" min={50} max={50000} step={10} disabled={!circle}
                  value={circle?.radiusM ?? ''} onChange={(e) => { if (circle) { setCircle({ ...circle, radiusM: Number(e.target.value) }); setShapeTouched(true); } }} /></label>
                {circle && circle.radiusM < 100 && <p className="hint warn">Under 100 m, a vehicle can drive through between two positions. Those visits are still recorded as "passed through".</p>}
              </>
            ) : (
              <>
                <p className="hint">Click on the map for each corner. Drag a corner to move it, or a midpoint to add one; double-click a corner to take it out.</p>
                <p>{points.length} corner{points.length === 1 ? '' : 's'}
                  {points.length > 0 && <> · <button type="button" className="link" onClick={() => { setPoints(points.slice(0, -1)); setShapeTouched(true); }}>Undo last corner</button>
                    {' '}· <button type="button" className="link" onClick={() => { setPoints([]); setShapeTouched(true); }}>Start again</button></>}</p>
              </>
            )}
          </fieldset>
          {cur && <p className="hint">{areaText(cur.areaM2)} · {ownerText(cur)} · {cur.insideNow} vehicle{cur.insideNow === 1 ? '' : 's'} inside now
            {shapeTouched && ' · a new shape counts from when it is saved; past entries and exits stay as they were'}</p>}
          {error && <p className="error" role="alert">{error}</p>}
          <div className="actions">
            <button className="primary" disabled={busy}>{busy ? 'Saving…' : id === null ? 'Add geofence' : 'Save'}</button>
            <button type="button" className="link" onClick={() => back()}>Cancel</button>
            {id !== null && !confirmRemove && <button type="button" className="link danger" onClick={() => setConfirmRemove(true)}>Remove…</button>}
          </div>
          {confirmRemove && (
            <div className="confirm" role="alert">
              <p>Remove <strong>{cur!.name}</strong>? It stops applying now. Past entries and exits keep its name.</p>
              <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>Remove geofence</button>
              <button type="button" className="link" onClick={() => setConfirmRemove(false)}>Keep it</button>
            </div>
          )}
        </div>
        <div className="card geo-edit-map">
          <div className="geo-search">
            <label className="sr-only" htmlFor="g-search">Find a place</label>
            <input id="g-search" type="search" placeholder="Find a place or address…" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void findPlace(e); }} />
            <button type="button" onClick={(e) => void findPlace(e)} disabled={search.trim().length < 3}>Find</button>
          </div>
          {found && found.length !== 1 && (
            <ul className="geo-found">
              {found.length === 0 && <li className="muted">Nothing found.</li>}
              {found.map((r) => <li key={r.label}><button type="button" className="link" onClick={() => { setGoTo({ key: Date.now(), ...r }); setFound(null); }}>{r.label}</button></li>)}
            </ul>
          )}
          {engine === null && <div className="map" />}
          {engine === 'osm' && <p className="notice">Drawing geofences needs the Google map, which isn't available here. Existing geofences still show on the other maps.</p>}
          {engine && engine !== 'osm' && (
            <GeofenceEditor cfg={engine.google} kind={kind} circle={circle} points={points} colour={colour} goTo={goTo} others={others}
              onCircle={(c) => { setCircle(c); setShapeTouched(true); }} onPoints={(p) => { setPoints(p); setShapeTouched(true); }} />
          )}
        </div>
      </form>
      {cur && (
        <section className="card">
          <h2>Latest entries and exits</h2>
          {cur.events.length === 0 ? <p className="muted">None yet.</p> : (
            <table className="table">
              <thead><tr><th>When</th><th>Vehicle</th><th>What</th></tr></thead>
              <tbody>{cur.events.map((e, i) => (
                <tr key={i}>
                  <td className="nowrap">{dateTime(e.at)}</td>
                  <td><Link to={`/vehicles/${e.vehicle.id}`}>{e.vehicle.name}</Link></td>
                  <td>{e.kind === 'enter' ? 'Arrived' : 'Left'}{e.passedThrough && <span className="muted"> (passed through, time estimated)</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
